import React from 'react';

const Skills: React.FC = () => {
  const skillCategories = [
    {
      title: 'Programming Languages',
      skills: [
        { name: 'C#', icon: 'fab fa-microsoft', level: 90 },
        { name: 'C', icon: 'fas fa-code', level: 85 },
        { name: 'Java', icon: 'fab fa-java', level: 80 },
        { name: 'JavaScript', icon: 'fab fa-js-square', level: 85 },
        { name: 'PHP', icon: 'fab fa-php', level: 75 }
      ]
    },
    {
      title: 'Web Technologies & Frameworks',
      skills: [
        { name: 'HTML5', icon: 'fab fa-html5', level: 95 },
        { name: 'CSS3', icon: 'fab fa-css3-alt', level: 90 },
        { name: 'Angular', icon: 'fab fa-angular', level: 80 },
        { name: 'Bootstrap', icon: 'fab fa-bootstrap', level: 85 },
        { name: 'Tailwind CSS', icon: 'fas fa-palette', level: 90 }
      ]
    },
    {
      title: 'APIs & Integration',
      skills: [
        { name: 'REST API', icon: 'fas fa-exchange-alt', level: 90 },
        { name: 'SOAP API', icon: 'fas fa-server', level: 85 },
        { name: 'Postman', icon: 'fas fa-vial', level: 90 },
        { name: 'Soap UI', icon: 'fas fa-plug', level: 80 },
        { name: 'API Integration', icon: 'fas fa-link', level: 85 }
      ]
    },
    {
      title: 'Tools & Platforms',
      skills: [
        { name: 'Visual Studio', icon: 'fas fa-code', level: 95 },
        { name: 'VS Code', icon: 'fas fa-code', level: 95 },
        { name: 'Salesforce', icon: 'fab fa-salesforce', level: 85 },
        { name: 'DataDog', icon: 'fas fa-chart-line', level: 85 },
        { name: 'ServiceNow', icon: 'fas fa-cogs', level: 80 }
      ]
    },
    {
      title: 'Databases & Cloud',
      skills: [
        { name: 'MS SQL Server', icon: 'fas fa-database', level: 90 },
        { name: 'MySQL', icon: 'fas fa-database', level: 85 },
        { name: 'PostgreSQL', icon: 'fas fa-database', level: 80 },
        { name: 'Azure', icon: 'fab fa-microsoft', level: 80 },
        { name: 'Git/GitHub', icon: 'fab fa-git-alt', level: 90 }
      ]
    },
    {
      title: 'AI & Automation Tools',
      skills: [
        { name: 'ChatGPT', icon: 'fas fa-robot', level: 95 },
        { name: 'Claude', icon: 'fas fa-brain', level: 95 },
        { name: 'Cursor', icon: 'fas fa-code', level: 90 },
        { name: 'Prompt Engineering', icon: 'fas fa-magic', level: 95 },
        { name: 'Microsoft Copilot', icon: 'fab fa-microsoft', level: 90 }
      ]
    }
  ];

  return (
    <section id="skills" className="py-20 bg-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 fade-in">
            My <span className="gradient-text">Skills</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto fade-in">
            Here are the technologies and tools I work with to bring ideas to life
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {skillCategories.map((category, categoryIndex) => (
            <div key={categoryIndex} className="fade-in">
              <h3 className="text-2xl font-bold text-white mb-8 flex items-center">
                <span className="w-10 h-10 bg-primary rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                  {categoryIndex + 1}
                </span>
                {category.title}
              </h3>
              
              <div className="space-y-4">
                {category.skills.map((skill, skillIndex) => (
                  <div key={skillIndex} className="skill-card bg-slate-800/50 backdrop-blur-sm p-5 rounded-xl border border-slate-700/50 hover:bg-slate-800/70 hover:border-primary/50 transition-all duration-300">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <i className={`${skill.icon} text-2xl text-primary mr-4`}></i>
                        <span className="text-white font-semibold text-base md:text-lg">{skill.name}</span>
                      </div>
                      <span className="text-gray-400 font-medium text-sm">{skill.level}%</span>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="w-full bg-slate-700 rounded-full h-2.5">
                      <div
                        className="bg-gradient-to-r from-primary to-secondary h-2.5 rounded-full transition-all duration-1000 ease-out"
                        style={{ width: `${skill.level}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Additional Skills Section */}
        <div className="mt-16 fade-in">
          <h3 className="text-2xl font-bold text-white mb-8 text-center">
            Additional Competencies
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { name: 'Agile/Scrum', icon: 'fas fa-sync-alt' },
              { name: 'Problem Solving', icon: 'fas fa-puzzle-piece' },
              { name: 'Technical Writing', icon: 'fas fa-pen' },
              { name: 'Team Leadership', icon: 'fas fa-users' },
              { name: 'Client Support', icon: 'fas fa-headset' },
              { name: 'Troubleshooting', icon: 'fas fa-wrench' },
              { name: 'System Analysis', icon: 'fas fa-search' },
              { name: 'JIRA', icon: 'fab fa-jira' },
              { name: 'Okta', icon: 'fas fa-shield-alt' },
              { name: 'New Relic', icon: 'fas fa-chart-bar' },
              { name: 'Windows', icon: 'fab fa-windows' },
              { name: 'Linux', icon: 'fab fa-linux' }
            ].map((skill, index) => (
              <div
                key={index}
                className="skill-card bg-slate-800/30 backdrop-blur-sm p-4 rounded-lg text-center border border-slate-700/50 hover:bg-slate-800/50 hover:border-primary/50 transition-all duration-300"
              >
                <i className={`${skill.icon} text-3xl text-primary mb-3 block`}></i>
                <span className="text-white text-xs md:text-sm font-medium">{skill.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Skills Summary */}
        <div className="mt-16 bg-slate-800/30 backdrop-blur-sm p-8 rounded-xl border border-slate-700/50 fade-in">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-white mb-4 flex items-center justify-center">
              <i className="fas fa-star text-primary mr-2"></i>
              Core Strengths
            </h3>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed max-w-4xl mx-auto">
              My technical expertise spans across multiple domains, from backend development with C# and SQL 
              to modern frontend technologies like React and TypeScript. With <strong>4+ years of experience</strong> in 
              technical support and development, I excel at <strong>REST and SOAP API integration</strong>, troubleshooting 
              complex systems, and leveraging AI tools for enhanced productivity. My experience across multiple companies 
              has given me strong problem-solving skills and the ability to communicate complex technical concepts clearly 
              to both technical and non-technical audiences.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;